import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { NavItem, mainNavItems } from './sidebarConfig';
import { hrNavItems } from './sidebarHrConfig';
import { financeNavItems, reportsNavItems, vesselNavItems, sectionIcons } from './sidebarFinanceConfig';

interface SidebarNavProps {
  hasPermission: (permission: string) => boolean;
  onClose: () => void;
}

const SidebarNav: React.FC<SidebarNavProps> = ({ hasPermission, onClose }) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    hr: true,
    finance: true
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const renderNavItem = (item: NavItem, isSubItem = false) => (
    <NavLink
      key={item.name}
      to={item.path}
      className={({ isActive }) =>
        `flex items-center ${isSubItem ? 'pl-12 py-2' : 'px-6 py-3'} text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors duration-200 ${
          isActive ? 'bg-primary-50 text-primary-600 border-r-4 border-primary-600' : ''
        }`
      }
      onClick={onClose}
    >
      {item.icon}
      <span className="ml-3 text-sm leading-tight">{item.name}</span>
    </NavLink>
  );

  const renderCollapsibleSection = (title: string, sectionKey: string, items: NavItem[], icon: React.ReactNode) => (
    <div key={sectionKey}>
      <button
        onClick={() => toggleSection(sectionKey)}
        className="w-full flex items-center justify-between px-6 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-600 transition-colors duration-200 font-medium"
      >
        <div className="flex items-center">
          {icon}
          <span className="ml-3">{title}</span>
        </div>
        <svg
          className={`w-4 h-4 transition-transform duration-200 ${expandedSections[sectionKey] ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expandedSections[sectionKey] && (
        <div className="bg-gray-50">
          {items
            .filter((item) => !item.permission || hasPermission(item.permission))
            .map((item) => renderNavItem(item, true))}
        </div>
      )}
    </div>
  );

  return (
    <nav className="mt-8 flex-1 overflow-y-auto">
      {mainNavItems
        .filter((item) => !item.permission || hasPermission(item.permission))
        .map((item) => renderNavItem(item))}

      {hrNavItems.some(item => !item.permission || hasPermission(item.permission)) && 
        renderCollapsibleSection('Human Resources', 'hr', hrNavItems, sectionIcons.hr)
      }

      {financeNavItems.some(item => !item.permission || hasPermission(item.permission)) && 
        renderCollapsibleSection('Finance', 'finance', financeNavItems, sectionIcons.finance)
      }

      {vesselNavItems.some(item => !item.permission || hasPermission(item.permission)) && 
        renderCollapsibleSection('Vessel', 'vessel', vesselNavItems, sectionIcons.vessel)
      }

      {reportsNavItems.some(item => !item.permission || hasPermission(item.permission)) && 
        renderCollapsibleSection('Reports', 'reports', reportsNavItems, sectionIcons.reports)
      }
    </nav>
  );
};

export default SidebarNav;
